/**
 * PasswordInput — drop-in replacement for <input type="password">.
 *
 * Adds the eye-icon reveal toggle and FIXES the cursor-jump bug that
 * happens when browsers switch the input's `type` attribute (Chrome,
 * Safari, and Firefox all reset selectionStart/selectionEnd to 0).
 *
 * Cursor fix
 * ──────────
 * On toggle:
 *   1. Snapshot the current selection (start, end) BEFORE the type flip.
 *   2. Wait one rAF + microtask so the browser finishes its reset.
 *   3. Restore the selection. If that fails (e.g. password manager
 *      reset the field), place the caret at the END of the value
 *      so typing continues naturally — never behind the letters.
 *
 * Props
 * ─────
 *   value, onChange, name, autoComplete, placeholder, disabled, required,
 *   className   → forwarded to the underlying <input>
 *   eyeClassName → extra classes on the eye button (optional)
 *   testId       → optional `data-testid` for the input (eye button gets
 *                  `${testId}-toggle`)
 *
 * Visual contract: SAME box size as a regular Tailwind input the consumer
 * wraps it in. The eye is absolute-positioned inside the right padding —
 * no layout shift. The consumer passes `className` exactly as they would
 * a plain <input>.
 */
import React, { forwardRef, useRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

const PasswordInput = forwardRef(function PasswordInput(
  {
    value,
    onChange,
    name,
    autoComplete = 'current-password',
    placeholder,
    disabled = false,
    required = false,
    className = '',
    eyeClassName = '',
    testId,
    ...rest
  },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const innerRef = useRef(null);
  const setRefs = (el) => {
    innerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  const toggle = () => {
    const el = innerRef.current;
    const start = el?.selectionStart ?? null;
    const end = el?.selectionEnd ?? null;
    const len = el?.value?.length ?? 0;
    setVisible((v) => !v);
    // Wait for React to flip `type`, then for the browser to settle.
    // Two layers of deferral so it works across browsers.
    requestAnimationFrame(() => {
      setTimeout(() => {
        const node = innerRef.current;
        if (!node) return;
        try {
          node.focus({ preventScroll: true });
          // Restore selection when possible; otherwise place caret at end.
          const s = start == null ? len : start;
          const e = end == null ? len : end;
          node.setSelectionRange(s, e);
        } catch {
          try { node.setSelectionRange(len, len); } catch { /* ignore */ }
        }
      }, 0);
    });
  };

  // Pad-right so text never runs under the eye icon. We don't override
  // any existing padding on the consumer's className except adding pr-10.
  const padded = className.includes('pr-') ? className : `${className} pr-10`;

  return (
    <div className="relative w-full">
      <input
        ref={setRefs}
        type={visible ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={padded}
        data-testid={testId}
        {...rest}
      />
      <button
        type="button"
        onClick={toggle}
        tabIndex={-1}
        aria-label={visible ? 'Hide password' : 'Show password'}
        data-testid={testId ? `${testId}-toggle` : undefined}
        className={`absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-700 ${eyeClassName}`}
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
});

export default PasswordInput;
